import * as Sentry from '@sentry/react';
import { useCheckoutHydration } from '@/hooks/useCheckoutHydration';
import { useCalculateOffers } from '@/pages/useCalculateOffers';
import { centsToMoney, pushDataLayerOnce } from '@/services/gtm-ecommerce.service';
import { getCheckoutSession, ordersService } from '@/services/orders.service';
import { useBookSkiPassStore } from '@/store/book-ski-pass.store';
import {
  applyCalculatedPeopleItemsToSlots,
  expandPassSelections,
  expandSelectedOffers,
  reconcileSlotsWithCalculatedOffers,
  type SkierSlot,
} from '@/utils/skier-slots';
import {
  isBirthDateMatchingAnyCategory,
  type DomainRestriction,
} from '@/utils/domain-restrictions';
import { calculatePercentageDiscountForItem, calculatePromoTotals } from '@/utils/promo-pricing';
import { isValidBirthDate } from '@/utils/booking';
import { buildSkicardAddonQueuesByDomainAge, takeSkicardAddonPrice } from '@/utils/skicard-pricing';
import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

export type PaymentPassItem = {
  slot: SkierSlot;
  firstName: string;
  lastName: string;
  days: number;
  unitPrice: number;
  discountAmount: number;
  currency: string;
};

type PaymentEcommerceItem = {
  item_id: string;
  item_name: string;
  priceInCents: number;
  quantity: number;
  currency: string;
};

function appendEcommerceItem(
  target: Map<string, PaymentEcommerceItem>,
  item: PaymentEcommerceItem
): void {
  const existing = target.get(item.item_id);
  if (existing) {
    existing.quantity += item.quantity;
    return;
  }
  target.set(item.item_id, item);
}

export function usePayment() {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const sessionId = searchParams.get('session_id') ?? undefined;
  const orderIdFromQuery = searchParams.get('orderId') ?? undefined;
  const exactlyReturn = searchParams.get('exactly') === 'return';
  const [exactlyOutcome, setExactlyOutcome] = useState<'pending' | 'paid' | 'failed'>('pending');

  useEffect(() => {
    if (!exactlyReturn) {
      return;
    }
    const session = getCheckoutSession();
    if (!session) {
      return;
    }
    let stopped = false;
    const tick = async () => {
      try {
        const result = await ordersService.getCardPaymentStatus(session.orderId, session.checkoutToken);
        if (!stopped) {
          setExactlyOutcome(result.status);
        }
      } catch {
        if (!stopped) {
          setExactlyOutcome('pending');
        }
      }
    };
    void tick();
    if (exactlyOutcome !== 'pending') {
      return () => {
        stopped = true;
      };
    }
    const timer = window.setInterval(() => {
      void tick();
    }, 4000);
    return () => {
      stopped = true;
      window.clearInterval(timer);
    };
  }, [exactlyReturn, exactlyOutcome]);

  const hasStripeRedirectParams = Boolean(sessionId || orderIdFromQuery);
  const returnCase = sessionId
    ? 'success'
    : exactlyReturn
      ? exactlyOutcome === 'paid'
        ? 'success'
        : exactlyOutcome === 'failed'
          ? 'failed'
          : 'pending'
      : orderIdFromQuery
        ? 'cancel'
        : null;

  useEffect(() => {
    if (returnCase !== 'failed' || !exactlyReturn) {
      return;
    }
    const session = getCheckoutSession();
    if (!session) {
      return;
    }
    let stopped = false;
    void ordersService
      .startCardPaymentFallback(session.orderId, session.checkoutToken)
      .then((result) => {
        if (!stopped && result.url) {
          window.location.assign(result.url);
        }
      })
      .catch((error: unknown) => {
        Sentry.captureException(error, {
          extra: { orderId: session.orderId, context: 'card_processor_fallback' },
        });
      });
    return () => {
      stopped = true;
    };
  }, [returnCase, exactlyReturn]);

  const selectedPassQuantities = useBookSkiPassStore((s) => s.selectedPassQuantities);
  const stepThreeFormFieldsValues = useBookSkiPassStore((s) => s.stepThreeFormFieldsValues);

  const {
    order: checkout,
    loading: checkoutLoading,
    error: checkoutError,
    refetch: refetchCheckout,
  } = useCheckoutHydration({
    hydrateStepThree: true,
    requiredSession: true,
  });

  const { dataByDomain, loading: offersLoading, error: offersError } = useCalculateOffers({
    passSelections: checkout?.passSelections,
  });
  // console.log('Selected pass quantities:', { selectedPassQuantities, stepThreeFormFieldsValues, dataByDomain });

  const skierForms = stepThreeFormFieldsValues.skierForms;

  const skierSlots = useMemo(() => {
    const fromCheckoutSelections = expandPassSelections(checkout?.passSelections ?? []).map(
      (slot, index) => ({
        ...slot,
        birthDate: skierForms[index]?.birthDate?.trim() || undefined,
      })
    );
    if (fromCheckoutSelections.length > 0) {
      const fromPeople = applyCalculatedPeopleItemsToSlots(
        fromCheckoutSelections,
        dataByDomain
      );
      return reconcileSlotsWithCalculatedOffers(fromPeople, dataByDomain);
    }
    const fromOffers = expandSelectedOffers(dataByDomain, selectedPassQuantities).map(
      (slot, index) => ({
        ...slot,
        birthDate: skierForms[index]?.birthDate?.trim() || undefined,
      })
    );
    return reconcileSlotsWithCalculatedOffers(fromOffers, dataByDomain);
  }, [checkout?.passSelections, dataByDomain, selectedPassQuantities, skierForms]);

  const priceBySlotKey = useMemo(() => {
    const map = new Map<string, { unitPrice: number; currency: string }>();
    for (const { domain, offers } of dataByDomain) {
      for (const item of offers.items) {
        if (!item) continue;
        const unitPrice = item.quantity > 0 ? Math.round(item.price / item.quantity) : item.price;
        const key =
          item.pass_pk != null
            ? `${String(domain.pk)}|${String(item.pass_pk)}`
            : `${String(domain.pk)}|${item.customer_type}|${item.title}`;
        map.set(key, { unitPrice, currency: offers.currency });
      }
    }
    return map;
  }, [dataByDomain]);

  const domainRestrictionsMap = useMemo(() => {
    const map = new Map<number, DomainRestriction[]>();
    for (const { domain } of dataByDomain) {
      if (domain.restrictions?.length) {
        map.set(domain.pk, domain.restrictions as DomainRestriction[]);
      }
    }
    return map;
  }, [dataByDomain]);

  const startDate = checkout?.tripDetails?.startDate ?? '';

  const invalidAgeBySlotIndex = useMemo(() => {
    return skierSlots.map((slot, i) => {
      const birthDate = skierForms[i]?.birthDate?.trim() ?? '';
      if (!birthDate || !isValidBirthDate(birthDate)) return false;
      const restrictions = domainRestrictionsMap.get(slot.domain.id) ?? [];
      return !isBirthDateMatchingAnyCategory(birthDate, restrictions, startDate);
    });
  }, [skierSlots, skierForms, domainRestrictionsMap, startDate]);

  const passItems: PaymentPassItem[] = useMemo(() => {
    const promoCodeInfo = checkout?.promoCodeInfo;
    const trip = checkout?.tripDetails;
    const days = trip?.skiDays ?? 0;
    const skicardAddonQueues = buildSkicardAddonQueuesByDomainAge(dataByDomain);

    if (Array.isArray(checkout?.items) && checkout.items.length > 0) {
      const pricedItems = checkout.items.map((item, index) => {
        const slot: SkierSlot = {
          index,
          ageCategory: item.ageCategory,
          passTitle: `${item.ageCategory} Pass`,
          domain: item.domain,
        };
        const domainPrefix = `${String(item.domain.id)}|`;
        const ageNorm = item.ageCategory.trim().toLowerCase();
        const domainEntries = [...priceBySlotKey.entries()].filter(([k]) => k.startsWith(domainPrefix));
        const priceEntry =
          domainEntries.find(([k]) => {
            const parts = k.slice(domainPrefix.length).split('|');
            return parts.some((p) => p.trim().toLowerCase() === ageNorm);
          })?.[1] ?? domainEntries[0]?.[1];
        const { unitPrice, currency } = priceEntry ?? { unitPrice: 0, currency: 'EUR' };

        return {
          slot,
          firstName: item.firstName,
          lastName: item.lastName,
          days: item.days ?? days,
          unitPrice,
          currency,
        };
      });

      const pricedItemsWithSkicard = pricedItems.map((item) => ({
        ...item,
        unitPrice:
          item.unitPrice +
          takeSkicardAddonPrice(skicardAddonQueues, item.slot.domain.id, item.slot.ageCategory),
      }));

      const subtotalInCents = pricedItemsWithSkicard.reduce((sum, item) => sum + item.unitPrice, 0);

      return pricedItemsWithSkicard.map((item) => {

        const discountAmount = calculatePercentageDiscountForItem(
          promoCodeInfo,
          item.unitPrice,
          item.slot.domain.id,
          subtotalInCents
        );

        return {
          slot: item.slot,
          firstName: item.firstName,
          lastName: item.lastName,
          days: item.days,
          unitPrice: item.unitPrice,
          discountAmount,
          currency: item.currency,
        };
      });
    }

    const pricedItems = skierSlots.map((slot, i) => {
      const form = skierForms[i] ?? { firstName: '', lastName: '', birthDate: '' };
      const primaryKey =
        slot.passOfferId != null
          ? `${String(slot.domain.id)}|${String(slot.passOfferId)}`
          : `${String(slot.domain.id)}|${slot.ageCategory}|${slot.passTitle}`;
      const priceEntry =
        priceBySlotKey.get(primaryKey) ??
        priceBySlotKey.get(`${String(slot.domain.id)}|${slot.ageCategory}|${slot.passTitle}`);
      const { unitPrice, currency } = priceEntry ?? { unitPrice: 0, currency: 'EUR' };

      return {
        slot,
        firstName: form.firstName,
        lastName: form.lastName,
        days,
        unitPrice,
        currency,
      };
    });

    const pricedItemsWithSkicard = pricedItems.map((item) => ({
      ...item,
      unitPrice:
        item.unitPrice +
        takeSkicardAddonPrice(skicardAddonQueues, item.slot.domain.id, item.slot.ageCategory),
    }));

    const subtotalInCents = pricedItemsWithSkicard.reduce((sum, item) => sum + item.unitPrice, 0);

    const allItems = pricedItemsWithSkicard.map((item) => {

      const discountAmount = calculatePercentageDiscountForItem(
        promoCodeInfo,
        item.unitPrice,
        item.slot.domain.id,
        subtotalInCents
      );

      return {
        slot: item.slot,
        firstName: item.firstName,
        lastName: item.lastName,
        days: item.days,
        unitPrice: item.unitPrice,
        discountAmount,
        currency: item.currency,
      };
    });
    return allItems.filter((_, i) => !invalidAgeBySlotIndex[i]);
  }, [
    skierSlots,
    skierForms,
    checkout?.tripDetails,
    checkout?.items,
    priceBySlotKey,
    checkout?.promoCodeInfo,
    invalidAgeBySlotIndex,
    dataByDomain,
  ]);

  const { subtotal, grandTotal, promoDiscountAmount, currency } = useMemo(() => {
    const totals = calculatePromoTotals(
      passItems.map((item) => ({
        unitPrice: item.unitPrice,
        domainId: item.slot.domain.id,
        discountAmount: item.discountAmount,
      })),
      checkout?.promoCodeInfo
    );

    return {
      subtotal: totals.subtotal,
      // Fixed price for a logged-in listed A2A account: the server price is what the card checkout charges.
      grandTotal: checkout?.fixedPrice?.amount ?? totals.grandTotal,
      promoDiscountAmount: totals.promoDiscountAmount,
      currency: passItems[passItems.length - 1]?.currency ?? 'EUR',
    };
  }, [passItems, checkout?.promoCodeInfo, checkout?.fixedPrice?.amount]);

  const paymentEcommerceItems = useMemo(() => {
    const itemsById = new Map<string, PaymentEcommerceItem>();

    for (const item of passItems) {
      const itemId =
        item.slot.passOfferId != null
          ? String(item.slot.passOfferId)
          : `${String(item.slot.domain.id)}:${item.slot.ageCategory}:${item.slot.passTitle}`;
      const itemName = `${item.slot.domain.name} ${item.slot.passTitle}`.trim();
      const existing = itemsById.get(itemId);
      if (existing) {
        existing.quantity += 1;
      } else {
        appendEcommerceItem(itemsById, {
          item_id: itemId,
          item_name: itemName,
          priceInCents: item.unitPrice,
          quantity: 1,
          currency: item.currency,
        });
      }
    }

    return [...itemsById.values()];
  }, [passItems]);

  useEffect(() => {
    if (hasStripeRedirectParams || !checkout || paymentEcommerceItems.length === 0) return;
    pushDataLayerOnce(
      `begin_checkout:${String(checkout.orderId)}`,
      {
        event: 'begin_checkout',
        ecommerce: {
          currency,
          value: centsToMoney(grandTotal),
          items: paymentEcommerceItems.map((item) => ({
            item_id: item.item_id,
            item_name: item.item_name,
            price: centsToMoney(item.priceInCents),
            quantity: item.quantity,
            currency: item.currency,
          })),
        },
      },
      {
        booking_ref: checkout.reference,
        promocode: checkout.promoCode,
      }
    );
  }, [hasStripeRedirectParams, checkout, paymentEcommerceItems, currency, grandTotal]);

  useEffect(() => {
    if (returnCase !== 'success' || checkoutLoading) return;
    const transactionId =
      orderIdFromQuery ?? (checkout?.orderId != null ? String(checkout.orderId) : sessionId);
    if (!transactionId) return;

    const items =
      paymentEcommerceItems.length > 0
        ? paymentEcommerceItems.map((item) => ({
            item_id: item.item_id,
            item_name: item.item_name,
            price: centsToMoney(item.priceInCents),
            quantity: item.quantity,
            currency: item.currency,
          }))
        : [
            {
              item_id: `order-${transactionId}`,
              item_name: 'Ski pass order',
              price: centsToMoney(grandTotal),
              quantity: 1,
              currency,
            },
          ];

    pushDataLayerOnce(
      `purchase:${transactionId}`,
      {
        event: 'purchase',
        ecommerce: {
          transaction_id: transactionId,
          currency,
          value: centsToMoney(grandTotal),
          items,
        },
      },
      {
        booking_ref: checkout?.reference,
        promocode: checkout?.promoCode,
      }
    );
  }, [
    returnCase,
    checkoutLoading,
    orderIdFromQuery,
    checkout?.orderId,
    checkout?.reference,
    checkout?.promoCode,
    sessionId,
    paymentEcommerceItems,
    currency,
    grandTotal,
  ]);

  return {
    checkout,
    loading: checkoutLoading || offersLoading,
    error: checkoutError ?? offersError,
    hasStripeRedirectParams,
    returnCase,
    passItems,
    subtotal,
    grandTotal,
    promoDiscountAmount,
    currency,
    promoCode: checkout?.promoCode ?? checkout?.promoCodeInfo?.code,
    promoCodeInfo: checkout?.promoCodeInfo,
    refetchCheckout,
  };
}
